/* ── Guardian Taunt Aura ──────────────────────────────────────────
 * Cyan rim-lit shield bubble + outward-radiating ring at the taunter's
 * position. Reads as "draw fire here" — a visible bulwark.
 *
 * Lifecycle scaffolding (life counter, dispose guard, end-of-life
 * cleanup) lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec2, vec3, vec4, float, length, smoothstep } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 2.0;
const SHIELD_RADIUS = 3.5;
const RING_MAX_SCALE = 8;
const COLOR_HEX = 0x4dffe9;

export class GuardianTauntAura extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly group: THREE.Group;
  private readonly shieldMesh: THREE.Mesh;
  private readonly shieldGeometry: THREE.SphereGeometry;
  private readonly shieldMaterial: THREE.MeshBasicNodeMaterial;
  private readonly ringMesh: THREE.Mesh;
  private readonly ringGeometry: THREE.RingGeometry;
  private readonly ringMaterial: THREE.MeshBasicNodeMaterial;
  private readonly uProgress: ReturnType<typeof uniform>;
  private readonly liveResolver?: LivePositionResolver;

  constructor(
    scene: THREE.Scene,
    position: Vec3Like,
    options?: { liveResolver?: LivePositionResolver },
  ) {
    super(scene, LIFE_SECONDS);
    this.group = new THREE.Group();
    this.group.position.set(position.x, position.y, position.z);
    this.liveResolver = options?.liveResolver;

    this.uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const uProgress = this.uProgress;

    // Rim-lit shield sphere — fresnel-style edge that pulses with progress.
    this.shieldGeometry = new THREE.SphereGeometry(SHIELD_RADIUS, 24, 16);
    this.shieldMaterial = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const shieldColor = Fn(() => {
      // UV.y proxies a vertical gradient; fade alpha + add a pulsing edge tint.
      const u = uv();
      const rim = smoothstep(float(0.0), float(0.4), u.y).mul(
        smoothstep(float(1.0), float(0.6), u.y),
      );
      const pulse = float(1.0).sub(float(uProgress).mul(0.7));
      const rgb = vec3(uColor).mul(rim.add(0.4));
      const alpha = rim.mul(0.6).mul(pulse);
      return vec4(rgb, alpha);
    })();
    this.shieldMaterial.colorNode = shieldColor;
    setEmissiveNode(this.shieldMaterial, shieldColor);
    this.shieldMesh = new THREE.Mesh(this.shieldGeometry, this.shieldMaterial);
    this.group.add(this.shieldMesh);

    // Outward-radiating ground ring. Lays flat in XZ.
    this.ringGeometry = new THREE.RingGeometry(0.7, 1.0, 64, 1);
    this.ringMaterial = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const ringColor = Fn(() => {
      const u = uv();
      const radial = length(u.sub(vec2(0.5, 0.5))).mul(2.0);
      const edge = smoothstep(float(0.5), float(1.0), radial);
      const rgb = vec3(uColor).add(vec3(0.2, 0.4, 0.4).mul(edge));
      const fade = float(uProgress).oneMinus();
      const alpha = edge.mul(fade).mul(0.7);
      return vec4(rgb, alpha);
    })();
    this.ringMaterial.colorNode = ringColor;
    setEmissiveNode(this.ringMaterial, ringColor);
    this.ringMesh = new THREE.Mesh(this.ringGeometry, this.ringMaterial);
    this.ringMesh.rotation.x = -Math.PI / 2;
    this.ringMesh.position.y = 0.5;
    this.ringMesh.scale.setScalar(0.001);
    this.group.add(this.ringMesh);

    this.scene.add(this.group);
  }

  protected tick(t: number): void {
    this.uProgress.value = t;
    this.applyLiveAnchor(this.group, this.liveResolver);
    // Ease-out ring expansion.
    const eased = 1 - (1 - t) * (1 - t);
    this.ringMesh.scale.setScalar(eased * RING_MAX_SCALE);
    // Subtle shield breathing.
    const breathe = 1 + Math.sin(this.life * 6) * 0.04;
    this.shieldMesh.scale.setScalar(breathe);
  }

  protected disposeResources(): void {
    this.scene.remove(this.group);
    this.shieldGeometry.dispose();
    this.shieldMaterial.dispose();
    this.ringGeometry.dispose();
    this.ringMaterial.dispose();
  }
}
